import cloneDeep from 'lodash/cloneDeep'
import orderBy from 'lodash/orderBy'
import { GIFT_WITH_PURCHASE_DEFAULTS } from './giftWithPurchaseDefaults'

export const cloneGiftWithPurchaseConfig = (config = GIFT_WITH_PURCHASE_DEFAULTS) => cloneDeep(config)

export const createDefaultGiftWithPurchaseDraft = () => cloneGiftWithPurchaseConfig(GIFT_WITH_PURCHASE_DEFAULTS)

export const getSortedTiers = (tiers = []) => orderBy(tiers, ['minAmount', 'id'], ['asc', 'asc'])

export const getSortedRewardGroups = (groups = []) => orderBy(groups, ['sortOrder', 'name'], ['asc', 'asc'])

export const formatAmountUa = (amount) =>
  Number(amount).toLocaleString('uk-UA', { maximumFractionDigits: 0 })

/**
 * Однозначний діапазон ступені.
 * Приклади: «До 3 000 грн», «Від 3 000 до < 5 000 грн», «Від 10 000 грн».
 * Верхня межа наступного порога не входить у поточну ступінь.
 */
export const getTierRangeLabel = (tier, sortedTiers = []) => {
  const index = sortedTiers.findIndex((item) => item.id === tier.id)
  const next = index >= 0 ? sortedTiers[index + 1] : null

  if (!next) {
    return `Від ${formatAmountUa(tier.minAmount)} грн`
  }

  if (index === 0) {
    return `До ${formatAmountUa(next.minAmount)} грн`
  }

  return `Від ${formatAmountUa(tier.minAmount)} до < ${formatAmountUa(next.minAmount)} грн`
}

export const getRewardGroupColumnLabel = (groupName) => `${String(groupName || '').trim() || '—'}, шт.`

/** Підпис для «Безкоштовно від»: нижній поріг і дія на вищі ступені. */
export const getTierFromAndAboveLabel = (tier) =>
  `Від ${formatAmountUa(tier.minAmount)} грн і вище`

export const buildTierSelectOptions = (tiers = []) => {
  const sorted = getSortedTiers(tiers)

  return sorted.map((tier) => ({
    value: tier.id,
    label: getTierRangeLabel(tier, sorted),
  }))
}

export const buildPackagingTierSelectOptions = (tiers = []) =>
  getSortedTiers(tiers).map((tier) => ({
    value: tier.id,
    label: getTierFromAndAboveLabel(tier),
  }))


export const createGiftEntityId = (prefix) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

export const emptyQuotasForGroups = (groups = []) =>
  groups.reduce((acc, group) => {
    acc[group.id] = 0
    return acc
  }, {})

/** Легка клієнтська перевірка для етапу 1А (взаємодії форми). */
export const validateGiftWithPurchaseDraft = (draft) => {
  const errors = []
  const tiers = draft?.tiers || []
  const groups = draft?.rewardGroups || []

  if (groups.length === 0) {
    errors.push('Потрібна хоча б одна група винагороди')
  }

  const groupNames = new Set()
  groups.forEach((group) => {
    const name = String(group.name || '').trim()
    if (!name) {
      errors.push('Назва групи не може бути порожньою')
      return
    }
    const key = name.toLowerCase()
    if (groupNames.has(key)) {
      errors.push(`Дубль назви групи: ${name}`)
    }
    groupNames.add(key)
  })

  if (tiers.length === 0) {
    errors.push('Потрібна хоча б одна ступінь')
  }

  const amounts = new Set()
  tiers.forEach((tier) => {
    const amount = tier.minAmount
    if (!Number.isInteger(amount) || amount < 0) {
      errors.push('Пороги мають бути невідʼємними цілими гривнями')
      return
    }
    if (amounts.has(amount)) {
      errors.push(`Повторюваний поріг: ${formatAmountUa(amount)} грн`)
    }
    amounts.add(amount)

    groups.forEach((group) => {
      const quota = tier.quotas?.[group.id]
      if (!Number.isInteger(quota) || quota < 0) {
        errors.push('Квоти мають бути цілими числами ≥ 0')
      }
    })
  })

  const freeFromTierId = draft?.packaging?.freeFromTierId
  if (freeFromTierId && !tiers.some((tier) => tier.id === freeFromTierId)) {
    errors.push('Ступінь безкоштовного пакування відсутня в довіднику')
  }

  return {
    ok: errors.length === 0,
    errors: [...new Set(errors)],
  }
}
